import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
interface ReceiptSummaryProps {
  totalThisReceipt: number;
  totalReceived: number;
  totalRemaining: number;
  poTotal: number;
  currency: string;
  locale?: string;
}
export function ReceiptSummary({
  totalThisReceipt,
  totalReceived,
  totalRemaining,
  poTotal,
  currency,
  locale = 'en-US'
}: ReceiptSummaryProps) {
  // Calculate percentages for progress bars
  const receivedPercentage = totalReceived / poTotal * 100;
  const thisReceiptPercentage = totalThisReceipt / poTotal * 100;
  const remainingPercentage = totalRemaining / poTotal * 100;
  // Calculate combined percentage (received + this receipt)
  const combinedPercentage = receivedPercentage + thisReceiptPercentage;
  // Check if we're at 100% after this receipt
  const isComplete = Math.abs(combinedPercentage - 100) < 0.01;
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14871">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14872">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14873">
          Receipt Summary
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-14874">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-14875">
          <div className="p-4 bg-green-50 rounded-lg" data-id="element-14876">
            <div className="flex justify-between items-center" data-id="element-14877">
              <h4 className="text-sm font-medium text-green-800" data-id="element-14878">
                Total This Receipt
              </h4>
              <ArrowUpIcon className="h-4 w-4 text-green-600" data-id="element-14879" />
            </div>
            <p className="text-2xl font-mono font-semibold text-green-700" data-id="element-14880">
              {formatCurrency(totalThisReceipt, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-14881">
              <div className="text-xs text-green-700 mb-1" data-id="element-14882">
                {thisReceiptPercentage.toFixed(1)}% of PO Total
              </div>
              <div className="w-full bg-green-200 rounded-full h-2" data-id="element-14883">
                <div className="bg-green-600 h-2 rounded-full" style={{
                width: `${thisReceiptPercentage}%`
              }} data-id="element-14884"></div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-14885">
            <div className="flex justify-between items-center" data-id="element-14886">
              <h4 className="text-sm font-medium text-blue-800" data-id="element-14887">
                Total Received To Date
              </h4>
              <CheckCircleIcon className="h-4 w-4 text-blue-600" data-id="element-14888" />
            </div>
            <p className="text-2xl font-mono font-semibold text-blue-700" data-id="element-14889">
              {formatCurrency(totalReceived, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-14890">
              <div className="text-xs text-blue-700 mb-1" data-id="element-14891">
                {receivedPercentage.toFixed(1)}% of PO Total
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2" data-id="element-14892">
                <div className="bg-blue-600 h-2 rounded-full" style={{
                width: `${receivedPercentage}%`
              }} data-id="element-14893"></div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg" data-id="element-14894">
            <div className="flex justify-between items-center" data-id="element-14895">
              <h4 className="text-sm font-medium text-amber-800" data-id="element-14896">
                Remaining After Receipt
              </h4>
              {isComplete ? <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-14897" /> : <AlertTriangleIcon className="h-4 w-4 text-amber-600" data-id="element-14898" />}
            </div>
            <p className="text-2xl font-mono font-semibold text-amber-700" data-id="element-14899">
              {formatCurrency(totalRemaining - totalThisReceipt, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-14900">
              <div className="text-xs text-amber-700 mb-1" data-id="element-14901">
                {(remainingPercentage - thisReceiptPercentage).toFixed(1)}% of
                PO Total
              </div>
              <div className="w-full bg-amber-200 rounded-full h-2" data-id="element-14902">
                <div className="bg-amber-600 h-2 rounded-full" style={{
                width: `${remainingPercentage - thisReceiptPercentage}%`
              }} data-id="element-14903"></div>
              </div>
            </div>
          </div>
        </div>
        {/* Combined progress bar */}
        <div className="mt-6" data-id="element-14904">
          <div className="flex justify-between items-center mb-2" data-id="element-14905">
            <h4 className="text-sm font-medium text-gray-700" data-id="element-14906">
              Overall PO Receipt Progress
            </h4>
            <div className="text-sm text-gray-500" data-id="element-14907">
              PO Total: {formatCurrency(poTotal, currency, locale)}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden" data-id="element-14908">
            <div className="flex h-full" data-id="element-14909">
              <div className="bg-blue-600 h-4" style={{
              width: `${receivedPercentage}%`
            }} data-id="element-14910"></div>
              <div className="bg-green-500 h-4" style={{
              width: `${thisReceiptPercentage}%`
            }} data-id="element-14911"></div>
            </div>
          </div>
          <div className="flex mt-2 text-xs" data-id="element-14912">
            <div className="flex items-center mr-4" data-id="element-14913">
              <div className="w-3 h-3 bg-blue-600 rounded-full mr-1" data-id="element-14914"></div>
              <span data-id="element-14915">Already Received ({receivedPercentage.toFixed(1)}%)</span>
            </div>
            <div className="flex items-center mr-4" data-id="element-14916">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-1" data-id="element-14917"></div>
              <span data-id="element-14918">This Receipt ({thisReceiptPercentage.toFixed(1)}%)</span>
            </div>
            <div className="flex items-center" data-id="element-14919">
              <div className="w-3 h-3 bg-gray-300 rounded-full mr-1" data-id="element-14920"></div>
              <span data-id="element-14921">Remaining ({(100 - combinedPercentage).toFixed(1)}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}